'use client';

import { useEffect, useRef } from 'react';

import { useAui, useAuiEvent, useAuiState } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { useConfigValue } from '@/components/providers/config-provider';
import { resolveThreadStatusKey, useThreadStatusStore } from '@/lib/thread-status/store';

/**
 * The backend titles server-persistence threads with a fire-and-forget LLM call ~1s after a run
 * finishes — and DEFERS titling greeting-only threads to a later exchange. assistant-ui only
 * generates a title once (on a new thread's first runEnd, before the backend has titled), so the
 * sidebar row goes stale until reload. `generateTitle()` re-runs our AssistantCloudServer
 * adapter, which prefers the server's current title and only updates local aui state — it never
 * PATCHes — so re-invoking it after every runEnd is a safe, loop-free refresh. Two delayed
 * attempts per run; a thread the backend deliberately left untitled just keeps its display
 * fallback.
 */
const TITLE_SYNC_ATTEMPT_DELAYS_MS = [2000, 5000];

export function useServerThreadTitleSync(): void {
  const isServerPersistence = useConfigValue<'local' | 'server'>('persistence') === 'server';
  const aui = useAui();
  const auiRef = useLatest(aui);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(new Map());
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);

  const scheduleTitleSync = (threadId: string) => {
    timersRef.current.get(threadId)?.forEach(clearTimeout);

    const refreshTitle = () => {
      try {
        const item = auiRef.current.threads.item({ id: threadId });
        // 'new' threads (not yet initialized) make generateTitle REJECT asynchronously — an
        // unhandled rejection inside aui's thread-list core — so guard and swallow both ways.
        if (item.getState().status === 'new') return;
        void Promise.resolve(item.generateTitle()).catch(() => {});
      } catch {
        // Thread archived/deleted — nothing to refresh.
      }
    };

    timersRef.current.set(
      threadId,
      TITLE_SYNC_ATTEMPT_DELAYS_MS.map(delay => setTimeout(refreshTitle, delay)),
    );
  };
  const scheduleTitleSyncRef = useLatest(scheduleTitleSync);

  useAuiEvent('thread.runEnd', ({ threadId }: { threadId: string }) => {
    if (!isServerPersistence) return;
    scheduleTitleSync(threadId);
  });

  // Also sync on switching into a thread (incl. the post-reload restore): a run that finished
  // server-side while the page was away emits no client runEnd, so its title would stay stale.
  const mainHasRemote = useAuiState(state =>
    state.threads.threadItems.some(
      item => item.id === state.threads.mainThreadId && !!item.remoteId,
    ),
  );

  useEffect(() => {
    if (!isServerPersistence || !mainThreadId || !mainHasRemote) return;
    scheduleTitleSyncRef.current(mainThreadId);
  }, [isServerPersistence, mainThreadId, mainHasRemote, scheduleTitleSyncRef]);

  // Background-thread completion: a run that finishes on a thread the user switched AWAY from
  // emits no client runEnd, so the effects above never fire for it and its title stays default.
  // The background-liveness poll flips its status to 'unread' when it finalizes server-side;
  // react to that and title it too (mapping the store's durable-uuid key back to the aui item id
  // that generateTitle resolves by).
  const threadItems = useAuiState(state => state.threads.threadItems);
  const threadItemsRef = useLatest(threadItems);
  const unreadKeys = useThreadStatusStore(state =>
    Object.entries(state.statuses)
      .filter(([, status]) => status === 'unread')
      .map(([key]) => key)
      .join(','),
  );
  const titledUnreadRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isServerPersistence) return;
    const keys = unreadKeys.split(',').filter(Boolean);
    const seen = titledUnreadRef.current;
    for (const key of keys) {
      if (seen.has(key)) continue;
      seen.add(key);
      const item = threadItemsRef.current.find(t => resolveThreadStatusKey(t) === key);
      if (item?.remoteId) scheduleTitleSyncRef.current(item.id);
    }
    for (const key of [...seen]) if (!keys.includes(key)) seen.delete(key);
  }, [isServerPersistence, unreadKeys, threadItemsRef, scheduleTitleSyncRef]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(threadTimers => threadTimers.forEach(clearTimeout));
  }, []);
}
